import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Snackbar,
  Stack,
  Switch,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { getNotificationStatus, updateNotificationOptIn } from "../../api";
import { logError } from "../../logging/loggingService";
import { trackPushDelivery } from "../../logging/telemetryService";

const generateDeviceToken = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `web-${Math.random().toString(36).slice(2)}`;
};

function getStoredToken() {
  try {
    const token = localStorage.getItem("pushDeviceToken");
    if (token) return token;
    const generated = generateDeviceToken();
    localStorage.setItem("pushDeviceToken", generated);
    return generated;
  } catch (error) {
    logError("Unable to access localStorage for push token", error, {
      component: "PushOptInCard",
    });
    return generateDeviceToken();
  }
}

export default function PushOptInCard() {
  const [status, setStatus] = useState({
    loading: true,
    optIn: false,
    lastUpdate: null,
    error: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const deviceToken = useMemo(() => getStoredToken(), []);
  const [expanded, setExpanded] = useState(true);

  const loadStatus = useCallback(async () => {
    setStatus((prev) => ({ ...prev, loading: true }));
    try {
      const response = await getNotificationStatus();
      const entries = response?.data?.subscriptions ?? [];
      const byToken = entries.find(
        (entry) => entry.deviceToken === deviceToken,
      );
      const activeEntry = byToken ?? entries[0];
      setStatus({
        loading: false,
        optIn: Boolean(activeEntry?.optIn),
        lastUpdate: activeEntry?.lastOptInAt,
        error: "",
      });
    } catch (error) {
      logError("Failed to load notification status", error, {
        component: "PushOptInCard",
      });
      setStatus({
        loading: false,
        optIn: false,
        lastUpdate: null,
        error: "Status konnte nicht geladen werden.",
      });
    }
  }, [deviceToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const toggleOptIn = async () => {
    const next = !status.optIn;
    try {
      const locale =
        typeof navigator !== "undefined" ? navigator.language : "de-DE";
      const deviceName =
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
      await updateNotificationOptIn({
        deviceToken,
        platform: "WEB",
        optIn: next,
        locale,
        deviceName,
      });
      setStatus((prev) => ({ ...prev, optIn: next, error: "" }));
      setSnackbar({
        open: true,
        message: next
          ? "Push-Benachrichtigungen aktiviert"
          : "Push-Benachrichtigungen deaktiviert",
        severity: "success",
      });
      trackPushDelivery(deviceToken, "WEB", next, {
        action: "opt-in-toggle",
      }).catch(() => {});
    } catch (error) {
      logError("Failed to update notification settings", error, {
        component: "PushOptInCard",
      });
      setSnackbar({
        open: true,
        message: "Aktualisierung fehlgeschlagen",
        severity: "error",
      });
    }
  };

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Card>
      <CardHeader
        avatar={
          <NotificationsActiveRoundedIcon color="primary" fontSize="large" />
        }
        title="Push-Benachrichtigungen"
        subheader="Bleiben Sie über neue Rechnungen und Aufgaben informiert"
        action={
          <Tooltip
            title={expanded ? "Bereich einklappen" : "Bereich ausklappen"}
          >
            <IconButton
              onClick={toggleExpanded}
              aria-expanded={expanded}
              aria-label={
                expanded ? "Bereich einklappen" : "Bereich ausklappen"
              }
              size="small"
            >
              {expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </IconButton>
          </Tooltip>
        }
      />
      <Collapse in={expanded} timeout="auto">
        <CardContent>
          {status.loading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={24} />
              <Typography>Prüfe Status …</Typography>
            </Stack>
          ) : status.error ? (
            <Alert severity="warning">{status.error}</Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2">
                Push-Benachrichtigungen erinnern Sie an fällige Rechnungen,
                Onboarding-Schritte und wichtige Systemmeldungen.
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography component="span">
                  Benachrichtigungen aktivieren
                </Typography>
                <Switch
                  checked={status.optIn}
                  onChange={toggleOptIn}
                  color="primary"
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Geräte-ID: {deviceToken}
              </Typography>
            </Stack>
          )}
        </CardContent>
        <CardActions>
          <Button size="small" onClick={loadStatus} disabled={status.loading}>
            Aktualisieren
          </Button>
        </CardActions>
      </Collapse>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
